import { useState, useEffect } from "react";
import { PageLayout } from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Key, 
  Users, 
  Shield,
  Search,
  Eye,
  EyeOff,
  Save,
  UserCog
} from "lucide-react";

export default function PasswordManagementPage({ evaluator, onLogout }) {
  const [users, setUsers] = useState({ evaluators: [], players: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`);
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
      setLoading(false);
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPassword(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setNewPassword("");
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error("Digite uma nova senha");
      return;
    }

    if (newPassword.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres");
      return;
    }

    setSaving(true);
    try {
      const endpoint = selectedUser.type === "evaluator" 
        ? `${API}/admin/change-password/evaluator/${selectedUser.id}`
        : `${API}/admin/change-password/player/${selectedUser.id}`;
      
      await axios.put(endpoint, { new_password: newPassword });
      
      toast.success(`Senha de ${selectedUser.name} alterada com sucesso!`);
      closeDialog();
    } catch (error) {
      toast.error("Erro ao alterar senha");
    }
    setSaving(false);
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: "bg-red-500/20 text-red-500 border-red-500/30",
      tecnico: "bg-blue-500/20 text-blue-500 border-blue-500/30",
      avaliador: "bg-purple-500/20 text-purple-500 border-purple-500/30",
      jogador: "bg-green-500/20 text-green-500 border-green-500/30"
    };
    return colors[role] || colors.avaliador;
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Admin",
      tecnico: "Técnico",
      avaliador: "Avaliador",
      jogador: "Jogador"
    };
    return labels[role] || role;
  };

  // Filter users based on search
  const filteredEvaluators = users.evaluators.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPlayers = users.players.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <PageLayout evaluator={evaluator} onLogout={onLogout} title="Gerenciar Senhas">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout evaluator={evaluator} onLogout={onLogout} title="Gerenciar Senhas">
      {/* Password Change Dialog - Single instance outside the loop */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {selectedUser?.type === "evaluator" ? (
                <UserCog className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Users className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">{selectedUser?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedUser?.email || "Sem email"}</p>
              </div>
              <Badge variant="outline" className={`ml-auto ${getRoleBadge(selectedUser?.role)}`}>
                {getRoleLabel(selectedUser?.role)}
              </Badge>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Nova Senha</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite a nova senha..."
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={saving || !newPassword}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvando..." : "Salvar Senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evaluators/Admins/Tecnicos */}
        <Card className="bg-card/80 backdrop-blur-md border border-border rounded-xl shadow-2xl">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-500" />
              </div>
              Avaliadores & Técnicos
              <Badge variant="outline" className="ml-auto">{filteredEvaluators.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[500px] overflow-y-auto">
            <div className="divide-y divide-border">
              {filteredEvaluators.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  Nenhum usuário encontrado
                </div>
              ) : (
                filteredEvaluators.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <UserCog className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email || "Sem email"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getRoleBadge(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { openPasswordDialog({...user, type: "evaluator"}); }}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        data-testid="change-password-btn"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Players */}
        <Card className="bg-card/80 backdrop-blur-md border border-border rounded-xl shadow-2xl">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-500" />
              </div>
              Jogadores
              <Badge variant="outline" className="ml-auto">{filteredPlayers.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[500px] overflow-y-auto">
            <div className="divide-y divide-border">
              {filteredPlayers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  Nenhum jogador encontrado
                </div>
              ) : (
                filteredPlayers.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Users className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email || "Sem email"}</p>
                        {user.password_plain && (
                          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
                            <Key className="h-3 w-3" />
                            Senha: <span className="font-mono bg-green-500/20 px-1 rounded">{user.password_plain}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.has_password ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30 text-xs">
                          Com senha
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 text-xs">
                          Sem senha
                        </Badge>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { openPasswordDialog({...user, type: "player"}); }}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        data-testid="change-password-btn"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
